import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { supabase } from "../../config/supabase";
import { setLocalStorage } from "../../helper/localStorage";
import { useAuth } from "../../hooks/useAuth";
import { oauthOrigin } from "../../services/agentsService";
// My Files' theme tokens: this page renders outside the .excalidraw container,
// where the editor's CSS variables don't exist.
import "../my-files/my-files.scss";
import "./oauth-consent.scss";

/**
 * `/oauth/consent?state=…` — the consent step of the agent login.
 * `/oauth/authorize` parked the agent's request under `state`; Allow / Deny
 * POST the decision to `/oauth/grant` as the signed-in user, and the page
 * follows the agent's loopback URL it answers with.
 *
 * Logged out, sign-in returns to this exact URL so the parked request is still
 * there; the URL must be allowed in the Supabase project's "Redirect URLs".
 */

type Pending =
  | { status: "loading" }
  | { status: "expired" }
  | { status: "ready"; clientName: string }
  | { status: "done"; decision: Decision }
  // `clientName` is kept when the request is still parked server-side, so
  // "Try again" can go straight back to the choice instead of refetching.
  | { status: "error"; message: string; clientName?: string };

type Decision = "allow" | "deny";

const CheckIcon = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CrossIcon = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const ClockIcon = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

const AlertIcon = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v5M12 16h.01" />
  </svg>
);

const AppMark = () => (
  <div className="oauth-consent__mark">
    <img src="/favicon.svg" alt="" />
  </div>
);

export const OAuthConsentPage: React.FC = () => {
  const [params] = useSearchParams();
  const state = params.get("state") ?? "";
  const { session, authResolved, signOut } = useAuth();
  const [pending, setPending] = useState<Pending>({ status: "loading" });
  const [busy, setBusy] = useState<Decision | null>(null);
  // Bumped by "Try again" after a failed load to re-run the fetch.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!state) {
      setPending({ status: "expired" });
      return;
    }
    let cancelled = false;
    setPending({ status: "loading" });
    fetch(`${oauthOrigin()}/oauth/grant?state=${encodeURIComponent(state)}`)
      .then(async (response) => {
        if (cancelled) {
          return;
        }
        if (response.status === 410) {
          setPending({ status: "expired" });
          return;
        }
        if (!response.ok) {
          throw new Error(`Could not load the request (${response.status}).`);
        }
        const body = (await response.json()) as { client_name: string };
        setPending({ status: "ready", clientName: body.client_name });
      })
      .catch((error: Error) => {
        if (!cancelled) {
          setPending({ status: "error", message: error.message });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [state, attempt]);

  const decide = async (decision: Decision, clientName: string) => {
    if (!session) {
      return;
    }
    setBusy(decision);
    try {
      const response = await fetch(`${oauthOrigin()}/oauth/grant`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        // The function accepts only an explicit "allow" / "deny".
        body: JSON.stringify({ state, decision }),
      });
      if (response.status === 410) {
        setPending({ status: "expired" });
        return;
      }
      if (!response.ok) {
        throw new Error(`The request failed (${response.status}).`);
      }
      const { redirect_to } = (await response.json()) as {
        redirect_to: string;
      };
      setPending({ status: "done", decision });
      window.location.assign(redirect_to);
    } catch (error) {
      setPending({
        status: "error",
        message: (error as Error).message,
        clientName,
      });
    } finally {
      setBusy(null);
    }
  };

  const retry = () => {
    if (pending.status === "error" && pending.clientName) {
      setPending({ status: "ready", clientName: pending.clientName });
    } else {
      setAttempt((n) => n + 1);
    }
  };

  const signInHere = async () => {
    // Mirrors `useAuth().signIn`, returning here instead of `/`; the flag is
    // what lets the app store the Notion token after this login.
    setLocalStorage("isUserLoggedIn", "false");
    const result = await supabase.auth.signInWithOAuth({
      provider: "notion",
      options: { redirectTo: window.location.href },
    });
    if (result.error) {
      setPending({ status: "error", message: result.error.message });
    }
  };

  let body: React.ReactNode;
  if (pending.status === "loading" || !authResolved) {
    body = (
      <>
        <AppMark />
        <p className="oauth-consent__loading" role="status">
          <span className="oauth-consent__spinner" aria-hidden="true" />
          Loading…
        </p>
      </>
    );
  } else if (pending.status === "expired") {
    body = (
      <>
        <div className="oauth-consent__mark oauth-consent__mark--muted">
          {ClockIcon}
        </div>
        <h1 className="oauth-consent__title">This request expired</h1>
        <p className="oauth-consent__lead">Start again from your agent.</p>
        <a
          href="/"
          className="oauth-consent__btn oauth-consent__btn--secondary oauth-consent__single"
        >
          Start again
        </a>
      </>
    );
  } else if (pending.status === "error") {
    body = (
      <>
        <div className="oauth-consent__mark oauth-consent__mark--muted">
          {AlertIcon}
        </div>
        <h1 className="oauth-consent__title">Something went wrong</h1>
        <p className="oauth-consent__lead">
          Nothing was changed. Check your connection and try again.
        </p>
        <p role="alert" className="oauth-consent__error">
          {pending.message}
        </p>
        <button
          type="button"
          onClick={retry}
          className="oauth-consent__btn oauth-consent__btn--primary oauth-consent__single"
        >
          Try again
        </button>
      </>
    );
  } else if (pending.status === "done") {
    body =
      pending.decision === "allow" ? (
        <>
          <div className="oauth-consent__mark oauth-consent__mark--ok">
            {CheckIcon}
          </div>
          <h1 className="oauth-consent__title">Connected</h1>
          <p className="oauth-consent__lead">
            You can close this tab and return to your agent.
          </p>
        </>
      ) : (
        <>
          <div className="oauth-consent__mark oauth-consent__mark--muted">
            {CrossIcon}
          </div>
          <h1 className="oauth-consent__title">Access denied</h1>
          <p className="oauth-consent__lead">You can close this tab.</p>
        </>
      );
  } else if (!session) {
    body = (
      <>
        <AppMark />
        <h1 className="oauth-consent__title">
          Sign in to connect {pending.clientName}
        </h1>
        <p className="oauth-consent__lead">
          {pending.clientName} wants to read and edit your maps. Sign in to
          choose whether to allow it.
        </p>
        <button
          type="button"
          onClick={signInHere}
          className="oauth-consent__btn oauth-consent__btn--primary oauth-consent__single"
        >
          Sign in with Notion
        </button>
      </>
    );
  } else {
    const { clientName } = pending;
    body = (
      <>
        <AppMark />
        <h1 className="oauth-consent__title">
          {clientName} wants to access your Mindmap
        </h1>
        <div className="oauth-consent__scope">
          <span className="oauth-consent__scope-check">{CheckIcon}</span>
          <div>
            <p className="oauth-consent__scope-name">Read and edit your maps</p>
            <p className="oauth-consent__scope-note">
              It acts as you, so anything it creates or changes is under your
              account.
            </p>
          </div>
        </div>
        <p className="oauth-consent__account">
          Signed in as <strong>{session.user?.email}</strong>.{" "}
          <button
            type="button"
            className="oauth-consent__link-button"
            disabled={busy !== null}
            onClick={() => void signOut()}
          >
            Not you? Sign out
          </button>
        </p>
        <div className="oauth-consent__actions">
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => decide("deny", clientName)}
            className="oauth-consent__btn oauth-consent__btn--secondary"
          >
            {busy === "deny" && (
              <span className="oauth-consent__spinner" aria-hidden="true" />
            )}
            {busy === "deny" ? "Denying…" : "Deny"}
          </button>
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => decide("allow", clientName)}
            className="oauth-consent__btn oauth-consent__btn--primary"
          >
            {busy === "allow" && (
              <span className="oauth-consent__spinner" aria-hidden="true" />
            )}
            {busy === "allow" ? "Connecting…" : "Allow"}
          </button>
        </div>
      </>
    );
  }

  return (
    <div data-testid="oauth-consent" className="oauth-consent">
      <div className="oauth-consent__column">{body}</div>
    </div>
  );
};

export default OAuthConsentPage;
